import { useMemo, useState, useRef, useEffect } from "react";

import { Check, Eye, X, ChevronDown, ChevronUp } from "lucide-react";
import { useReplaceTextController } from "../../hooks/useReplaceTextController";
import { AiComponentData, ReplaceTextData, replaceTextDataSchema } from "../../types/AiComponent";
import { BlockMatch } from "../../../lib/llm-ui";
import { parseJson5 } from "../../../lib/json5";
import { showToast } from "../../utils/toast";

export function ReplaceTextBlock({ blockMatch }: { blockMatch: BlockMatch }) {
  if (!blockMatch.isVisible) {
    return null;
  }

  const { data, error } = replaceTextDataSchema.safeParse(parseJson5(blockMatch.output));

  if (error) {
    return null;
  }

  return <ReplaceText {...data} />;
}

export function ReplaceText(props: ReplaceTextData) {
  const { accept, reject, status, select, notFound } = useReplaceTextController(props);
  const { text, update } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const fusedNodes = useMemo(() => {
    if (!text && !update) {
      return null;
    }

    if (text === update) {
      return <span key="identical">{text}</span>;
    }

    if (!text) {
      return <CorrectionText text={update} />;
    }

    if (!update) {
      return <CrossedOutText text={text} />;
    }

    return [
      <CrossedOutText key={`del`} text={text} />,
      <CorrectionText key={`ins`} text={update} />,
    ];
  }, [text, update]);

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is updated
    const rafId = requestAnimationFrame(() => {
      if (textRef.current) {
        const element = textRef.current;
        // Temporarily remove max-height constraint to measure natural height
        // Store original inline styles
        const originalMaxHeight = element.style.maxHeight;
        const originalOverflow = element.style.overflow;

        // Remove constraints to measure natural height
        element.style.maxHeight = "none";
        element.style.overflow = "visible";
        const naturalHeight = element.scrollHeight;

        // Restore original inline styles (Tailwind classes remain in className)
        element.style.maxHeight = originalMaxHeight;
        element.style.overflow = originalOverflow;

        // Check if natural height exceeds the collapsed max height (96px = 24 * 4px)
        const maxCollapsedHeight = 96;
        setShowExpandButton(naturalHeight > maxCollapsedHeight);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [fusedNodes]);

  if (notFound) {
    // showToast("Not found: " + JSON.stringify(props), "warning");
    return null;
  }

  return (
    <div className="w-full flex flex-col items-start justify-between flex-wrap py-2">
      <div className="w-full relative pb-4">
        <p
          ref={textRef}
          className={`m-0 whitespace-pre-wrap text-sm pr-8 transition-all ${
            !isExpanded && showExpandButton ? "max-h-24 overflow-hidden" : ""
          }`}
        >
          {fusedNodes}
        </p>
        {showExpandButton && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-xs text-base-content/70 hover:text-base-content flex items-center gap-1"
            type="button"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Afficher moins
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                Afficher plus
              </>
            )}
          </button>
        )}
      </div>
      <div className="self-stretch flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={select}
          className="btn btn-secondary btn-outline btn-sm border-base-300 hover:border-transparent focus:border-transparent flex items-center"
          type="button"
          title="Sélectionner dans le document"
        >
          <Eye className="w-4 h-4" />
        </button>
        {text === update ? null : status === "pending" ? (
          <div className="flex items-center gap-2">
            <button
              onClick={reject}
              className="btn btn-secondary btn-outline btn-sm border-base-300 hover:border-transparent focus:border-transparent"
              type="button"
            >
              Rejeter
            </button>
            <button
              onClick={accept}
              className="btn btn-secondary btn-sm flex items-center"
              type="button"
            >
              <Check className="w-5 h-5 -ml-1" />
              Appliquer
            </button>
          </div>
        ) : (
          <span
            className="btn btn-sm flex items-center gap-1 shadow-none cursor-default pointer-events-none"
            style={{ minWidth: 88, justifyContent: "center" }}
          >
            {status === "accepted" ? (
              <>
                <Check className="w-5 h-5 -ml-1" />
                Appliqué
              </>
            ) : (
              <>
                <X className="w-5 h-5 -ml-1" />
                Rejeté
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

interface SimpleTextProps {
  text: string;
}

function CorrectionText({ text }: SimpleTextProps) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="h-auto text-left font-bold text-[#2563eb] underline">{text}</span>
    </div>
  );
}

function CrossedOutText({ text }: SimpleTextProps) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="h-auto text-left font-bold text-[#d0383b] line-through">{text}</span>
    </div>
  );
}
